import { useState } from "react";
import I from "./Icons";
import Dropdown from "./Dropdown";
import { CHARTS, DATE_RANGE_OPTIONS, PERCENTAGE_MAP, REVERSE_PCT } from "./constants";
import { useTables } from "../report-builder";
import FormulaBuilder from "./FormulaBuilder";

interface GraphCardProps {
    graph: any;
    onUpdate: (graph: any) => void;
    onDelete: () => void;
    onDuplicate: () => void;
    onMoveUp: () => void;
    onMoveDown: () => void;
    isFirst: boolean;
    isLast: boolean;
    canDelete: boolean;
    theme: any;
    sectionRepeat?: string;
    sectionRepeats?: string[];
}

// ─── Small reusable pieces ───
const Label = ({ children, theme }: { children: string; theme: any }) => (
    <div style={{
        fontSize: 11, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: ".08em", color: theme.textSecondary, marginBottom: 4,
    }}>
        {children}
    </div>
);

const Toggle = ({ label, checked, onChange, theme, disabled = false }: {
    label: string; checked: boolean; onChange: (v: boolean) => void; theme: any; disabled?: boolean;
}) => (
    <label style={{
        display: "flex", alignItems: "center", gap: 6,
        padding: "6px 10px", background: theme.cardBackground,
        border: `1px solid ${theme.border}`, borderRadius: 6,
        cursor: disabled ? "default" : "pointer", fontSize: 13,
        opacity: disabled ? 0.5 : 1,
    }}>
        <input type="checkbox" checked={checked} disabled={disabled}
               onChange={e => onChange(e.target.checked)} style={{ cursor: disabled ? "default" : "pointer" }} />
        <span style={{ color: theme.text, fontWeight: 500 }}>{label}</span>
    </label>
);

const TextInput = ({ value, onChange, placeholder, theme, disabled = false }: {
    value: string; onChange: (v: string) => void; placeholder?: string; theme: any; disabled?: boolean;
}) => (
    <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
           disabled={disabled}
           style={{
               width: "100%", padding: "6px 10px", background: theme.cardBackground,
               border: `1px solid ${theme.border}`, borderRadius: 6, color: theme.text,
               fontSize: 13, outline: "none", opacity: disabled ? 0.5 : 1,
           }}
    />
);

// ─── Graph Card ───
function GraphCard({ graph, onUpdate, onDelete, onDuplicate, onMoveUp, onMoveDown, isFirst, isLast, canDelete, theme, sectionRepeat, sectionRepeats }: GraphCardProps) {
    const [open, setOpen] = useState(false);
    const TABLES = useTables();
    const tbl = TABLES.find(t => t.id === graph.dataTable);
    const ci = CHARTS.find(c => c.id === graph.chartType);
    const CIcon = I[ci?.ic as keyof typeof I] || I.Bar;
    const filtered = graph.filterContext !== "None";

    // ─── Derived state ───
    const isFormula = graph.aggregation === "formula";
    // Base chart type (strip percentage/snapshot variants)
    const baseChartType = REVERSE_PCT[graph.chartType] ||
        (graph.chartType === "table_snapshot" ? "table" : graph.chartType);
    const isNormalizedPct = !!REVERSE_PCT[graph.chartType];
    const isSnapshot = graph.chartType === "table_snapshot";

    const isTable = baseChartType === "table";
    const isBar = baseChartType === "stacked_bar" || baseChartType === "grouped_bar";
    const isLine = baseChartType === "line";
    const isArea = graph.chartType === "area";
    const isWaterfall = graph.chartType === "waterfall";
    const isChart = !isTable; // any visual chart
    const hasGrouping = graph.grouping !== "None";
    // Enforce: legend is meaningless (and should be off) when there is no grouping
    const effectiveShowLegend = hasGrouping && (graph.showLegend ?? false);

    // ─── Dynamic grouping options ───
    const dynamicGroupings = (() => {
        const opts = ["None"];
        if (tbl?.text_fields && Array.isArray(tbl.text_fields)) {
            tbl.text_fields.forEach((f: string) => opts.push(`By ${f.replace(/_/g, " ")}`));
        }
        if (sectionRepeat && sectionRepeat !== "None") {
            opts.push(`Entity (${sectionRepeat})`, `Entity (${sectionRepeat}) & Others`);
        }
        opts.push("Top 10 + Others", "Top 5 + Others");
        return opts;
    })();

    // ─── Dynamic filter options ───
    const dynamicFilters = (() => {
        const opts = ["None"];
        if (tbl?.text_fields && Array.isArray(tbl.text_fields)) {
            tbl.text_fields.forEach((f: string) => {
                opts.push(`${f.replace(/_/g, " ")} Only`);
            });
        }
        if (sectionRepeat && sectionRepeat !== "None") {
            opts.push(`Entity (${sectionRepeat}) Only`);
        }
        if (tbl?.text_fields && Array.isArray(tbl.text_fields) && tbl.text_fields.length > 1) {
            for (let i = 0; i < tbl.text_fields.length; i++) {
                for (let j = 0; j < tbl.text_fields.length; j++) {
                    if (i !== j) {
                        const a = tbl.text_fields[i].replace(/_/g, " ");
                        const b = tbl.text_fields[j].replace(/_/g, " ");
                        opts.push(`${a} Filter → ${b} Group`);
                    }
                }
            }
        }
        return opts;
    })();

    // ─── Collapsed header summary ───
    const headerSummary = isFormula
        ? "Formula"
        : graph.yAxisField.replace(/_/g, " ");

    return (
        <div style={{
            background: theme.background, border: `1px solid ${theme.border}`,
            borderRadius: 8, overflow: "visible",
            ...(filtered ? { borderLeft: `2.5px solid ${theme.warning}` } : {}),
        }}>
            {/* ═══ Collapsed header ═══ */}
            <div style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "7px 10px", cursor: "pointer", userSelect: "none",
            }} onClick={() => setOpen(!open)}>
        <span style={{ color: theme.textSecondary }}>
          {open ? <I.Down /> : <I.Right />}
        </span>
                <div style={{
                    width: 22, height: 22, borderRadius: 5, display: "flex",
                    alignItems: "center", justifyContent: "center",
                    background: `${theme.blue}20`, color: theme.blue, flexShrink: 0,
                }}>
                    <CIcon />
                </div>
                <input value={graph.label}
                       onChange={e => onUpdate({ ...graph, label: e.target.value })}
                       onClick={e => e.stopPropagation()}
                       style={{
                           flex: 1, background: "transparent", border: "none", color: theme.text,
                           fontSize: 13, fontWeight: 600, padding: 0, outline: "none", minWidth: 0,
                       }}
                />
                {filtered && (
                    <span style={{
                        display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11,
                        padding: "1px 6px", borderRadius: 8, background: `${theme.warning}20`,
                        color: theme.warning, fontWeight: 600, whiteSpace: "nowrap",
                    }}>
            <I.Filter /> {graph.filterContext.split("→")[0].trim()}
          </span>
                )}
                <span style={{ fontSize: 11, color: theme.textSecondary, whiteSpace: "nowrap" }}>
          {headerSummary}
        </span>
                <button
                    onClick={e => { e.stopPropagation(); onMoveUp(); }}
                    disabled={isFirst}
                    style={{ background: "none", border: "none", color: isFirst ? theme.border : theme.textSecondary, cursor: isFirst ? "default" : "pointer", padding: 2, display: "flex" }}
                >
                    <I.Up />
                </button>
                <button
                    onClick={e => { e.stopPropagation(); onMoveDown(); }}
                    disabled={isLast}
                    style={{ background: "none", border: "none", color: isLast ? theme.border : theme.textSecondary, cursor: isLast ? "default" : "pointer", padding: 2, display: "flex" }}
                >
                    <I.Dn />
                </button>
                <button
                    onClick={e => { e.stopPropagation(); onDuplicate(); }}
                    style={{ background: "none", border: "none", color: theme.textSecondary, cursor: "pointer", padding: 2, display: "flex" }}
                >
                    <I.Copy />
                </button>
                {canDelete && (
                    <button onClick={e => { e.stopPropagation(); onDelete(); }}
                            style={{
                                background: "none", border: "none", color: theme.textSecondary,
                                cursor: "pointer", padding: 2, display: "flex",
                            }}
                            onMouseEnter={e => e.currentTarget.style.color = theme.error}
                            onMouseLeave={e => e.currentTarget.style.color = theme.textSecondary}
                    >
                        <I.Trash />
                    </button>
                )}
            </div>

            {/* ═══ Expanded panel ═══ */}
            {open && (
                <div style={{
                    padding: "6px 10px 12px", borderTop: `1px solid ${theme.border}`,
                    display: "flex", flexDirection: "column", gap: 10,
                }}>

                    {/* ── Chart Type ── */}
                    <div>
                        <Label theme={theme}>Chart Type</Label>
                        <div style={{ display: "flex", gap: 4 }}>
                            {CHARTS.map(ct => {
                                const Ic = I[ct.ic as keyof typeof I];
                                const on = baseChartType === ct.id;
                                return (
                                    <button key={ct.id}
                                            onClick={() => {
                                                // Preserve percentage toggle when switching base type
                                                let newType = ct.id;
                                                if (isNormalizedPct && PERCENTAGE_MAP[ct.id]) {
                                                    newType = PERCENTAGE_MAP[ct.id];
                                                }
                                                const update: any = { ...graph, chartType: newType };
                                                // Initialize waterfallConfig with defaults when switching to waterfall
                                                if (ct.id === "waterfall" && !graph.waterfallConfig) {
                                                    update.waterfallConfig = {
                                                        value_column: "Subs",
                                                        color_total: "rgb(60, 20, 80)",
                                                        color_increase: "rgb(80, 160, 60)",
                                                        color_decrease: "rgb(200, 30, 50)",
                                                        connector_color: "#9898a4",
                                                        connector_dash: "dot",
                                                        show_brackets: true,
                                                        show_group_boxes: true,
                                                    };
                                                }
                                                onUpdate(update);
                                            }}
                                            style={{
                                                flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                                                gap: 3, padding: "7px 4px", borderRadius: 6, cursor: "pointer",
                                                background: on ? `${theme.blue}20` : theme.cardBackground,
                                                border: `1.5px solid ${on ? theme.blue : theme.border}`,
                                                color: on ? theme.blue : theme.textSecondary, fontSize: 11, fontWeight: 500,
                                            }}>
                                        <Ic />{ct.name}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Chart variant toggles — inline below chart type buttons */}
                        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                            {PERCENTAGE_MAP[baseChartType] && !isSnapshot && (
                                <Toggle label="Normalized Percentages" theme={theme}
                                        checked={isNormalizedPct}
                                        onChange={v => {
                                            const newType = v ? PERCENTAGE_MAP[baseChartType] : baseChartType;
                                            onUpdate({ ...graph, chartType: newType });
                                        }}
                                />
                            )}
                            {baseChartType === "table" && !isNormalizedPct && (
                                <Toggle label="Performance Snapshot View" theme={theme}
                                        checked={isSnapshot}
                                        onChange={v => {
                                            const newType = v ? "table_snapshot" : "table";
                                            onUpdate({ ...graph, chartType: newType });
                                        }}
                                />
                            )}
                        </div>
                    </div>

                    {/* ── Single Reporting Period Notice (waterfall only) ── */}
                    {isWaterfall && (
                        <div style={{
                            display: "flex", alignItems: "center", gap: 6, padding: "8px 10px",
                            background: `${theme.blue}11`, borderRadius: 6, fontSize: 12,
                            color: theme.blue, border: `1px solid ${theme.blue}33`,
                        }}>
                            <div style={{ flexShrink: 0, width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <I.Bulb />
                            </div>
                            <span>
                                <strong>Single Period Chart:</strong> Waterfall graphs display data
                                from one reporting period only (the most recent or filtered period).
                                Use section repeat to show multiple periods across pages.
                            </span>
                        </div>
                    )}

                    {/* ── Data Table + Y-Axis ── */}
                    {!isWaterfall && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                        <div style={{ opacity: isFormula ? 0.45 : 1, pointerEvents: isFormula ? "none" : "auto" }}>
                            <Dropdown label="Data Table" value={graph.dataTable}
                                      options={TABLES.map(t => ({ v: t.id, l: t.name }))}
                                      onChange={v => {
                                          const nt = TABLES.find(t => t.id === v);
                                          const update: any = {
                                              ...graph, dataTable: v,
                                              yAxisField: nt?.numeric_fields[0] || graph.yAxisField,
                                          };
                                          if (isFormula && graph.formula?.steps) {
                                              const valid = new Set(nt?.numeric_fields || []);
                                              const fallback = nt?.numeric_fields?.[0] || "";
                                              update.formula = {
                                                  steps: graph.formula.steps.map((s: any) => {
                                                      if (s.type === "field" && s.field && !valid.has(s.field))
                                                          return { ...s, field: fallback };
                                                      if (s.operand?.type === "field" && s.operand.field && !valid.has(s.operand.field))
                                                          return { ...s, operand: { ...s.operand, field: fallback } };
                                                      return s;
                                                  }),
                                              };
                                          }
                                          onUpdate(update);
                                      }}
                                      theme={theme}
                            />
                        </div>
                        <div style={{ opacity: isFormula ? 0.45 : 1, pointerEvents: isFormula ? "none" : "auto" }}>
                            <Dropdown label="Y-Axis Metric" value={graph.yAxisField}
                                      options={tbl?.numeric_fields.map((f: string) => ({ v: f, l: f.replace(/_/g, " ") })) || []}
                                      onChange={v => onUpdate({ ...graph, yAxisField: v })}
                                      theme={theme}
                            />
                        </div>
                    </div>
                    )}

                    {/* ── Aggregation + Grouping ── */}
                    {!isWaterfall && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                        <Dropdown label="Aggregation Method" value={graph.aggregation || "sum"}
                                  options={[
                                      { v: "sum", l: "Sum" },
                                      { v: "avg", l: "Average" },
                                      { v: "formula", l: "Formula" },
                                  ]}
                                  onChange={v => {
                                      const update: any = { ...graph, aggregation: v };
                                      if (v === "formula" && !graph.formula?.steps) {
                                          update.formula = {
                                              steps: [{ type: "field", field: tbl?.numeric_fields?.[0] || "", agg: "sum" }],
                                          };
                                      }
                                      onUpdate(update);
                                  }}
                                  theme={theme}
                        />
                        <Dropdown label="Grouping" value={graph.grouping}
                                  options={dynamicGroupings}
                                  onChange={v => onUpdate({
                                      ...graph,
                                      grouping: v,
                                      // Auto-clear legend when grouping is removed
                                      showLegend: v === "None" ? false : graph.showLegend,
                                  })}
                                  theme={theme}
                        />
                    </div>
                    )}

                    {/* ── Formula Builder (conditional) ── */}
                    {!isWaterfall && isFormula && (
                        <FormulaBuilder graph={graph} onUpdate={onUpdate} tbl={tbl} theme={theme} />
                    )}

                    {/* ── Filter Context ── */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                        <Dropdown label="Filter Context" value={graph.filterContext}
                                  options={dynamicFilters}
                                  onChange={v => onUpdate({ ...graph, filterContext: v })}
                                  theme={theme}
                        />
                    </div>

                    {/* ── Date Field + Month Range ── */}
                    <div style={{ display: "grid", gridTemplateColumns: isFormula ? "1fr" : "1fr 1fr", gap: 8 }}>
                        {!isFormula && (
                            <Dropdown label="Date Field" value={graph.dateField || "reporting_period"}
                                      options={tbl?.date_fields?.map((f: string) => ({ v: f, l: f.replace(/_/g, " ") })) || [{ v: "reporting_period", l: "Reporting Period" }]}
                                      onChange={v => onUpdate({ ...graph, dateField: v })}
                                      theme={theme}
                            />
                        )}
                        <Dropdown label="Month Range"
                                  value={
                                      DATE_RANGE_OPTIONS.find(o =>
                                          o.type
                                              ? o.type === graph.dateRangeType
                                              : o.start === graph.monthRangeStart && o.end === graph.monthRangeEnd
                                      )?.id || "all"
                                  }
                                  options={DATE_RANGE_OPTIONS.map(o => ({ v: o.id, l: o.name }))}
                                  onChange={v => {
                                      const sel = DATE_RANGE_OPTIONS.find(o => o.id === v);
                                      onUpdate({
                                          ...graph,
                                          monthRangeStart: sel?.start ?? undefined,
                                          monthRangeEnd:   sel?.end   ?? undefined,
                                          dateRangeType:   sel?.type  ?? null,
                                      });
                                  }}
                                  theme={theme}
                        />
                    </div>

                    {/* ── Axis Labels (charts only, not table) ── */}
                    {isChart && (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                            <div>
                                <Label theme={theme}>X-Axis Label</Label>
                                <TextInput value={graph.xAxisLabel || ""} placeholder="e.g., Month"
                                           onChange={v => onUpdate({ ...graph, xAxisLabel: v })} theme={theme} />
                            </div>
                            <div>
                                <Label theme={theme}>Y-Axis Label</Label>
                                <TextInput value={graph.yAxisLabel || ""} placeholder="e.g., Revenue (R)"
                                           onChange={v => onUpdate({ ...graph, yAxisLabel: v })} theme={theme} />
                            </div>
                        </div>
                    )}

                    {/* ── Number Formatting ── */}
                    <div>
                        <Label theme={theme}>Number Formatting</Label>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 6 }}>
                            <Dropdown label="Format" value={graph.numberFormat || "###"}
                                      options={[
                                          { v: "###", l: "### (whole numbers)" },
                                          { v: "###.0", l: "###.0 (1 decimal)" },
                                          { v: "###.00", l: "###.00 (2 decimals)" },
                                          { v: "###%", l: "###% (percentage)" },
                                      ]}
                                      onChange={v => onUpdate({ ...graph, numberFormat: v })}
                                      theme={theme}
                            />
                            <Dropdown label="Divisor / Multiplier" value={String(graph.divisor ?? 1)}
                                      options={[
                                          { v: "0.000001", l: "Millions (×1 000 000)" },
                                          { v: "0.001", l: "Thousands (×1 000)" },
                                          { v: "1", l: "None (×1)" },
                                          { v: "1000", l: "Thousands (÷1 000)" },
                                          { v: "1000000", l: "Millions (÷1 000 000)" },
                                      ]}
                                      onChange={v => {
                                          const divisor = Number(v);
                                          const autoSuffixes = ["", "K", "M"];
                                          const currentSuffix = graph.suffix || "";
                                          const shouldAutoFill = autoSuffixes.includes(currentSuffix);
                                          const newSuffix = shouldAutoFill
                                              ? (divisor === 1000 ? "K" : divisor === 1000000 ? "M" : "")
                                              : currentSuffix;
                                          onUpdate({ ...graph, divisor, suffix: newSuffix });
                                      }}
                                      theme={theme}
                            />
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                            <div>
                                <Label theme={theme}>Prefix</Label>
                                <TextInput value={graph.prefix || ""} placeholder='e.g. R  or  $'
                                           onChange={v => onUpdate({ ...graph, prefix: v })} theme={theme} />
                            </div>
                            <div>
                                <Label theme={theme}>Suffix</Label>
                                <TextInput value={graph.suffix || ""} placeholder='e.g. K  or  %'
                                           onChange={v => onUpdate({ ...graph, suffix: v })} theme={theme} />
                            </div>
                        </div>
                    </div>

                    {/* ── Display Toggles (charts only) ── */}
                    {isChart && (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 }}>
                            {/* Legend — only useful when there are groups */}
                            <Toggle label="Show Legend" theme={theme}
                                    checked={effectiveShowLegend}
                                    onChange={v => onUpdate({ ...graph, showLegend: v })}
                                    disabled={!hasGrouping}
                            />

                            {/* Bar totals — bar charts only */}
                            {isBar && (
                                <Toggle label="Bar Totals" theme={theme}
                                        checked={graph.showBarTotals ?? false}
                                        onChange={v => onUpdate({ ...graph, showBarTotals: v })}
                                />
                            )}

                            {/* Segment labels — bar charts only */}
                            {isBar && (
                                <Toggle label="Values in Bars" theme={theme}
                                        checked={graph.showSegmentLabels ?? false}
                                        onChange={v => onUpdate({ ...graph, showSegmentLabels: v })}
                                />
                            )}

                            {/* Line labels — line & area only */}
                            {(isLine || isArea) && (
                                <Toggle label="Line Labels" theme={theme}
                                        checked={graph.showLineLabels ?? false}
                                        onChange={v => onUpdate({ ...graph, showLineLabels: v })}
                                />
                            )}

                            {/* Highlight last point/bar */}
                            <Toggle label="Highlight Last" theme={theme}
                                    checked={graph.highlightLast ?? false}
                                    onChange={v => onUpdate({ ...graph, highlightLast: v })}
                            />

                            {/* Entity colors — only useful when there are groups */}
                            <Toggle label="Entity Colors" theme={theme}
                                    checked={graph.useEntityColors ?? false}
                                    onChange={v => onUpdate({ ...graph, useEntityColors: v })}
                                    disabled={!hasGrouping}
                            />

                            {/* Show entity only — percentage charts only */}
                            {isNormalizedPct && (
                                <Toggle label="Show Entity Only" theme={theme}
                                        checked={graph.showEntityOnly ?? false}
                                        onChange={v => onUpdate({ ...graph, showEntityOnly: v })}
                                />
                            )}
                        </div>
                    )}

                    {/* ── Display Toggles (table only) ── */}
                    {isTable && (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 }}>
                            {/* Totals row */}
                            <Toggle label="Show Totals" theme={theme}
                                    checked={graph.showTableTotals ?? true}
                                    onChange={v => onUpdate({ ...graph, showTableTotals: v })}
                            />
                            {/* Row totals column */}
                            <Toggle label="Show Row Totals" theme={theme}
                                    checked={graph.showTableRowTotals ?? false}
                                    onChange={v => onUpdate({ ...graph, showTableRowTotals: v })}
                            />
                        </div>
                    )}

                    {/* ── Trend Annotation (charts only) ── */}
                    {isChart && (
                        <div>
                            <Label theme={theme}>Trend Annotation</Label>
                            <div style={{
                                display: "flex", gap: 8, alignItems: "flex-end",
                                marginBottom: graph.trend?.enabled ? 8 : 0,
                            }}>
                                <div style={{ flex: 1 }}>
                                    <Toggle label="Enable Trend" theme={theme}
                                            checked={graph.trend?.enabled || false}
                                            onChange={v => onUpdate({
                                                ...graph, trend: { ...(graph.trend || {}), enabled: v },
                                            })}
                                    />
                                </div>
                                {graph.trend?.enabled && (
                                    <div style={{ flex: 2 }}>
                                        <Label theme={theme}>Custom Label</Label>
                                        <TextInput value={graph.trend?.label || graph.label}
                                                   placeholder="Custom trend label..."
                                                   onChange={v => onUpdate({
                                                       ...graph, trend: { ...(graph.trend || {}), label: v },
                                                   })}
                                                   theme={theme}
                                        />
                                    </div>
                                )}
                            </div>
                            {graph.trend?.enabled && (
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                    <Dropdown label="Calculation Mode"
                                              value={graph.trend?.calculationMode || "difference"}
                                              options={[
                                                  { v: "difference", l: "Difference (Change)" },
                                                  { v: "average", l: "Average" },
                                              ]}
                                              onChange={v => onUpdate({
                                                  ...graph, trend: { ...(graph.trend || {}), calculationMode: v },
                                              })}
                                              theme={theme}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Waterfall Configuration (waterfall only) ── */}
                    {isWaterfall && (
                        <div>
                            <Label theme={theme}>Waterfall Configuration</Label>
                            
                            {/* Product Group Filter */}
                            <div style={{ marginBottom: 12 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: theme.textSecondary, marginBottom: 4 }}>Product Group / Target Value</div>
                                <TextInput
                                    value={graph.waterfallConfig?.product_group || ""} 
                                    placeholder="e.g., Fibre, Copper (leave empty for section repeat)"
                                    onChange={v => onUpdate({
                                        ...graph, 
                                        waterfallConfig: { ...(graph.waterfallConfig || {}), product_group: v || undefined }
                                    })}
                                    theme={theme}
                                />
                                <div style={{ fontSize: 10, color: theme.textSecondary, marginTop: 4 }}>
                                    Target for filtering and iteration. Falls back to section repeat value if empty.
                                </div>
                            </div>

                            {/* Filter by Repeat Field */}
                            {(sectionRepeats || []).filter(f => f !== sectionRepeat).length > 0 && (
                                <div style={{ marginBottom: 12 }}>
                                    <Dropdown
                                        label="Filter by Repeat Field"
                                        value={graph.waterfallConfig?.repeat_filter_field || "None"}
                                        options={[
                                            { v: "None", l: "None (no additional filter)" },
                                            ...(sectionRepeats || [])
                                                .filter(f => f !== sectionRepeat)
                                                .map(f => ({ v: f, l: f }))
                                        ]}
                                        onChange={(v: string) => onUpdate({
                                            ...graph,
                                            waterfallConfig: {
                                                ...(graph.waterfallConfig || {}),
                                                repeat_filter_field: v === "None" ? null : v,
                                            },
                                        })}
                                        theme={theme}
                                    />
                                    <div style={{ fontSize: 10, color: theme.textSecondary, marginTop: 4 }}>
                                        Filter waterfall by the current value of another repeat field.
                                    </div>
                                </div>
                            )}

                            {/* Non-Iterating Sheets */}
                            <div style={{ marginBottom: 12 }}>
                                <div style={{
                                    fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                                    letterSpacing: ".08em", color: theme.textSecondary, marginBottom: 4,
                                }}>
                                    Non-Iterating Sheets <span style={{ color: theme.textSecondary, fontSize: 11 }}>(one bar each)</span>
                                </div>
                                <div style={{ fontSize: 10, color: theme.textSecondary, marginBottom: 8 }}>
                                    Each sheet creates exactly one waterfall bar. Add/remove freely.
                                </div>
                                
                                {(graph.waterfallConfig?.sheets || []).map((sheet: any, idx: number) => (
                                    <div key={idx} style={{
                                        border: `1px solid ${theme.border}`, borderRadius: 6,
                                        padding: 8, marginBottom: 6, background: theme.cardBackground,
                                    }}>
                                        <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1.5fr 1fr 30px", gap: 6 }}>
                                            <Dropdown label="Sheet" 
                                                      value={sheet.sheet || ""}
                                                      options={TABLES.map(t => ({ v: t.id, l: t.name }))}
                                                      onChange={v => {
                                                          const updated = [...(graph.waterfallConfig?.sheets || [])];
                                                          updated[idx] = { ...updated[idx], sheet: v };
                                                          onUpdate({ ...graph, waterfallConfig: { ...(graph.waterfallConfig || {}), sheets: updated } });
                                                      }}
                                                      theme={theme} />
                                            
                                            <div>
                                                <Label theme={theme}>Bar Label</Label>
                                                <TextInput value={sheet.bar_label || ""}
                                                           placeholder="e.g., OB, New"
                                                           onChange={v => {
                                                               const updated = [...(graph.waterfallConfig?.sheets || [])];
                                                               updated[idx] = { ...updated[idx], bar_label: v };
                                                               onUpdate({ ...graph, waterfallConfig: { ...(graph.waterfallConfig || {}), sheets: updated } });
                                                           }}
                                                           theme={theme} />
                                            </div>
                                            
                                            <Dropdown label="Value Column"
                                                      value={sheet.value_column || "Subs"}
                                                      options={(() => {
                                                          const selectedTable = TABLES.find(t => t.id === sheet.sheet);
                                                          return selectedTable?.numeric_fields?.map((f: string) => ({ 
                                                              v: f, 
                                                              l: f.replace(/_/g, " ") 
                                                          })) || [{ v: "Subs", l: "Subs" }];
                                                      })()}
                                                      onChange={v => {
                                                          const updated = [...(graph.waterfallConfig?.sheets || [])];
                                                          updated[idx] = { ...updated[idx], value_column: v };
                                                          onUpdate({ ...graph, waterfallConfig: { ...(graph.waterfallConfig || {}), sheets: updated } });
                                                      }}
                                                      theme={theme} />
                                            
                                            <Dropdown label="Type" value={sheet.measure || "increase"}
                                                      options={[
                                                          { v: "total", l: "Total" },
                                                          { v: "increase", l: "Increase" },
                                                          { v: "decrease", l: "Decrease" },
                                                      ]}
                                                      onChange={v => {
                                                          const updated = [...(graph.waterfallConfig?.sheets || [])];
                                                          updated[idx] = { ...updated[idx], measure: v };
                                                          onUpdate({ ...graph, waterfallConfig: { ...(graph.waterfallConfig || {}), sheets: updated } });
                                                      }}
                                                      theme={theme} />
                                            
                                            <button onClick={() => {
                                                        const updated = (graph.waterfallConfig?.sheets || []).filter((_: any, i: number) => i !== idx);
                                                        onUpdate({ ...graph, waterfallConfig: { ...(graph.waterfallConfig || {}), sheets: updated } });
                                                    }}
                                                    style={{
                                                        marginTop: 18, padding: "6px", background: "transparent",
                                                        border: `1px solid ${theme.error}`, color: theme.error,
                                                        borderRadius: 4, cursor: "pointer", fontSize: 14,
                                                    }}>
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                
                                <button onClick={() => {
                                            const sheets = graph.waterfallConfig?.sheets || [];
                                            onUpdate({
                                                ...graph,
                                                waterfallConfig: {
                                                    ...(graph.waterfallConfig || {}),
                                                    sheets: [...sheets, { 
                                                        sheet: "NewSheet", 
                                                        bar_label: "New Bar", 
                                                        value_column: "Subs", 
                                                        measure: "relative", 
                                                        sign: 1 
                                                    }]
                                                }
                                            });
                                        }}
                                        style={{
                                            width: "100%", padding: "8px", fontSize: 12,
                                            color: theme.blue, background: `${theme.blue}11`,
                                            border: `1px dashed ${theme.blue}`, borderRadius: 6,
                                            cursor: "pointer",
                                        }}>
                                    + Add Sheet Bar
                                </button>
                            </div>

                            {/* Iteration Config Toggle */}
                            <div style={{ marginBottom: 12 }}>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                                    <div style={{
                                        fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                                        letterSpacing: ".08em", color: theme.textSecondary,
                                    }}>
                                        Iteration Sheet <span style={{ color: theme.textSecondary, fontSize: 11 }}>(produces N bars)</span>
                                    </div>
                                    <Toggle label="Enable Iteration"
                                            theme={theme}
                                            checked={graph.waterfallConfig?.iteration_config !== null && graph.waterfallConfig?.iteration_config !== undefined}
                                            onChange={v => onUpdate({
                                                ...graph,
                                                waterfallConfig: {
                                                    ...(graph.waterfallConfig || {}),
                                                    iteration_config: v ? {
                                                        sheet: "ProductMigrations",
                                                        value_column: "Subs",
                                                        iteration_mode: "migration",
                                                    } : null
                                                }
                                            })} />
                                </div>
                                
                                {graph.waterfallConfig?.iteration_config && (
                                    <div style={{
                                        border: `1px solid ${theme.border}`, borderRadius: 6,
                                        padding: 10, background: `${theme.blue}05`,
                                    }}>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                                            <div>
                                                <Label theme={theme}>Sheet</Label>
                                                <TextInput 
                                                          value={graph.waterfallConfig.iteration_config.sheet || ""}
                                                          placeholder="ProductMigrations"
                                                          onChange={v => onUpdate({
                                                              ...graph,
                                                              waterfallConfig: {
                                                                  ...(graph.waterfallConfig || {}),
                                                                  iteration_config: { ...graph.waterfallConfig.iteration_config, sheet: v }
                                                              }
                                                          })}
                                                          theme={theme} />
                                            </div>
                                            
                                            <Dropdown label="Value Column"
                                                      value={graph.waterfallConfig.iteration_config.value_column || "Subs"}
                                                      options={(() => {
                                                          const iterSheet = TABLES.find(t => t.id === graph.waterfallConfig.iteration_config.sheet);
                                                          return iterSheet?.numeric_fields?.map((f: string) => ({ 
                                                              v: f, 
                                                              l: f.replace(/_/g, " ") 
                                                          })) || [
                                                              { v: "Subs", l: "Subs" },
                                                              { v: "Recognised_Revenue", l: "Recognised Revenue" },
                                                              { v: "Product_Revenue", l: "Product Revenue" },
                                                          ];
                                                      })()}
                                                      onChange={v => onUpdate({
                                                          ...graph,
                                                          waterfallConfig: {
                                                              ...(graph.waterfallConfig || {}),
                                                              iteration_config: { ...graph.waterfallConfig.iteration_config, value_column: v }
                                                          }
                                                      })}
                                                      theme={theme} />
                                        </div>
                                        
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginBottom: 8 }}>
                                            <Dropdown label="Mode"
                                                      value={graph.waterfallConfig.iteration_config.iteration_mode || "migration"}
                                                      options={[
                                                          { v: "migration", l: "Migration (Inbound/Outbound)" },
                                                          { v: "group_by", l: "Group By (Simple)" },
                                                      ]}
                                                      onChange={v => onUpdate({
                                                          ...graph,
                                                          waterfallConfig: {
                                                              ...(graph.waterfallConfig || {}),
                                                              iteration_config: { ...graph.waterfallConfig.iteration_config, iteration_mode: v }
                                                          }
                                                      })}
                                                      theme={theme} />
                                        </div>
                                        
                                        {graph.waterfallConfig.iteration_config.iteration_mode === "group_by" && (
                                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                                <div>
                                                    <Label theme={theme}>Group By Column</Label>
                                                    <TextInput
                                                               value={graph.waterfallConfig.iteration_config.group_by_column || ""}
                                                               placeholder="e.g., Region"
                                                               onChange={v => onUpdate({
                                                                   ...graph,
                                                                   waterfallConfig: {
                                                                       ...(graph.waterfallConfig || {}),
                                                                       iteration_config: { ...graph.waterfallConfig.iteration_config, group_by_column: v }
                                                                   }
                                                               })}
                                                               theme={theme} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Visual Options */}
                            <div style={{ marginBottom: 12 }}>
                                <Label theme={theme}>Visual Options</Label>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
                                    <div>
                                        <Label theme={theme}>Total Color</Label>
                                        <TextInput
                                            value={graph.waterfallConfig?.color_total || "rgb(60, 20, 80)"} 
                                            placeholder="#3c1450"
                                            onChange={v => onUpdate({
                                                ...graph, 
                                                waterfallConfig: { ...(graph.waterfallConfig || {}), color_total: v }
                                            })}
                                            theme={theme}
                                        />
                                    </div>
                                    <div>
                                        <Label theme={theme}>Increase Color</Label>
                                        <TextInput
                                            value={graph.waterfallConfig?.color_increase || "rgb(80, 160, 60)"} 
                                            placeholder="#50a03c"
                                            onChange={v => onUpdate({
                                                ...graph, 
                                                waterfallConfig: { ...(graph.waterfallConfig || {}), color_increase: v }
                                            })}
                                            theme={theme}
                                        />
                                    </div>
                                    <div>
                                        <Label theme={theme}>Decrease Color</Label>
                                        <TextInput
                                            value={graph.waterfallConfig?.color_decrease || "rgb(200, 30, 50)"} 
                                            placeholder="#c81e32"
                                            onChange={v => onUpdate({
                                                ...graph, 
                                                waterfallConfig: { ...(graph.waterfallConfig || {}), color_decrease: v }
                                            })}
                                            theme={theme}
                                        />
                                    </div>
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 }}>
                                    <Toggle 
                                        label="Show Brackets" 
                                        theme={theme}
                                        checked={graph.waterfallConfig?.show_brackets ?? true}
                                        onChange={v => onUpdate({
                                            ...graph, 
                                            waterfallConfig: { ...(graph.waterfallConfig || {}), show_brackets: v }
                                        })}
                                    />
                                    <Toggle 
                                        label="Show Group Boxes" 
                                        theme={theme}
                                        checked={graph.waterfallConfig?.show_group_boxes ?? true}
                                        onChange={v => onUpdate({
                                            ...graph, 
                                            waterfallConfig: { ...(graph.waterfallConfig || {}), show_group_boxes: v }
                                        })}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Active filter description ── */}
                    {filtered && (
                        <div style={{
                            display: "flex", alignItems: "center", gap: 5, padding: "6px 8px",
                            background: `${theme.warning}11`, borderRadius: 5, fontSize: 12, color: theme.warning,
                        }}>
                            <I.Filter />
                            <span>
                {graph.filterContext.includes("→")
                    ? `Filters by ${graph.filterContext.split("→")[0].trim().replace(" Filter", "").toLowerCase()}, then groups by ${graph.filterContext.split("→")[1].trim().replace(" Group", "").toLowerCase()}`
                    : graph.filterContext.includes("Entity")
                        ? `Data filtered to the repeat ${sectionRepeat?.toLowerCase()} (entity) only`
                        : `Data filtered to ${graph.filterContext.replace(" Only", "").toLowerCase()} only`
                }
              </span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default GraphCard;